import { LocalAudioTrack, LocalVideoTrack, Room, Track } from 'livekit-client'
import type { AudioCaptureOptions, VideoPreset } from 'livekit-client'
import type { CallSession } from './session'

/**
 * Enumerates the OS's current input/output devices via LiveKit's device API.
 * `requestMicPermissions`/`requestCameraPermissions` mirror LiveKit's own default (`true`): when
 * device labels aren't available yet, `Room.getLocalDevices` briefly opens a real mic/camera
 * stream just to unlock them, which is audible/visible to the user (lights up the camera's
 * hardware indicator). Kept as two independent flags — not one shared boolean — because evidence
 * that the mic was granted before says nothing about the camera (and vice versa); requesting the
 * camera's labels just because the mic happens to be unlocked would open the camera and light its
 * indicator for no reason the user asked for. `audiooutput` labels are gated by the mic flag,
 * matching Chromium/WebKit's own behavior of tying output-device labels to the audio permission.
 * Pass `false` for background refreshes (e.g. on startup) where an unlabeled device list is good
 * enough and shouldn't cost a permission grab.
 */
export async function enumerateDevices(requestMicPermissions = true, requestCameraPermissions = requestMicPermissions) {
  const [microphones, cameras, speakers] = await Promise.all([
    Room.getLocalDevices('audioinput', requestMicPermissions),
    Room.getLocalDevices('videoinput', requestCameraPermissions),
    Room.getLocalDevices('audiooutput', requestMicPermissions),
  ])
  return { microphones, cameras, speakers }
}

/**
 * Switches the active mic device. Falls back to restarting the published
 * track with the new device id when `switchActiveDevice` itself fails —
 * but only while the mic is actually live; if it's muted, a switch failure
 * is a real error and must propagate instead of silently no-op'ing.
 */
export async function switchMicrophoneDevice(
  session: CallSession,
  deviceId: string,
  isMicMuted: boolean,
  captureOptions: AudioCaptureOptions,
) {
  try {
    await session.room.switchActiveDevice('audioinput', deviceId)
  } catch (error) {
    if (isMicMuted) throw error
    const track = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
    if (!(track instanceof LocalAudioTrack)) throw error
    await track.restartTrack(captureOptions)
  }
}

/** Same fallback strategy as `switchMicrophoneDevice`, for the camera. */
export async function switchCameraDevice(
  session: CallSession,
  deviceId: string,
  isCameraEnabled: boolean,
  resolution: VideoPreset['resolution'],
) {
  try {
    await session.room.switchActiveDevice('videoinput', deviceId)
  } catch (error) {
    if (!isCameraEnabled) throw error
    const track = session.room.localParticipant.getTrackPublication(Track.Source.Camera)?.track
    if (!(track instanceof LocalVideoTrack)) throw error
    await track.restartTrack({ deviceId, resolution })
  }
}

export async function switchSpeakerDevice(session: CallSession, deviceId: string) {
  await session.room.switchActiveDevice('audiooutput', deviceId)
}
